/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
// 4x4 Sudoku puzzles (each region 2x2). Number 1-4. Pre-filled hints.
// Solution layout: row of solutions, hints (with 0 = empty)
const PUZZLES = [
  {
    sol: [[1,2,3,4],[3,4,1,2],[2,1,4,3],[4,3,2,1]],
    hints: [[1,0,3,0],[0,4,0,2],[2,0,4,0],[0,3,0,1]],
  },
  {
    sol: [[2,1,4,3],[3,4,1,2],[1,2,3,4],[4,3,2,1]],
    hints: [[2,0,0,3],[3,4,0,0],[0,0,3,4],[0,0,2,1]],
  },
  {
    sol: [[3,4,1,2],[1,2,3,4],[2,3,4,1],[4,1,2,3]],
    hints: [[0,4,1,0],[1,0,0,4],[0,3,0,1],[4,0,2,0]],
  },
  {
    sol: [[4,3,2,1],[2,1,4,3],[1,2,3,4],[3,4,1,2]],
    hints: [[4,0,0,1],[0,1,4,0],[1,0,0,4],[0,4,1,0]],
  },
  {
    sol: [[1,3,2,4],[2,4,1,3],[3,1,4,2],[4,2,3,1]],
    hints: [[1,0,2,0],[0,4,0,3],[3,0,4,0],[0,2,0,1]],
  },
  {
    sol: [[2,3,4,1],[4,1,2,3],[3,2,1,4],[1,4,3,2]],
    hints: [[0,3,0,1],[4,0,2,0],[0,2,0,4],[1,0,3,0]],
  },
  {
    sol: [[3,1,4,2],[2,4,1,3],[1,3,2,4],[4,2,3,1]],
    hints: [[3,1,0,0],[0,0,1,3],[1,3,0,0],[0,0,3,1]],
  },
  {
    sol: [[4,2,1,3],[3,1,2,4],[2,4,3,1],[1,3,4,2]],
    hints: [[4,2,0,0],[0,0,2,4],[2,4,0,0],[0,0,4,2]],
  },
  {
    sol: [[1,4,3,2],[3,2,1,4],[4,3,2,1],[2,1,4,3]],
    hints: [[1,0,0,2],[0,2,1,0],[0,3,2,0],[2,0,0,3]],
  },
  {
    sol: [[2,4,3,1],[1,3,4,2],[4,2,1,3],[3,1,2,4]],
    hints: [[2,0,0,1],[0,3,4,0],[0,2,1,0],[3,0,0,4]],
  },
];

const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const SudokuAnak = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [puzzleIdx, setPuzzleIdx] = useState(0);
  const [grid, setGrid] = useState(() => PUZZLES[0].hints.map(r => [...r]));
  const [selected, setSelected] = useState(null);
  const [errorCells, setErrorCells] = useState({});
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);

  const puzzle = PUZZLES[puzzleIdx];

  const isHint = (r, c) => puzzle.hints[r][c] !== 0;

  const tryPlace = (n) => {
    if (!selected || won) return;
    const { r, c } = selected;
    if (isHint(r, c)) return;
    const correct = puzzle.sol[r][c];
    const next = grid.map(row => [...row]);
    next[r][c] = n;
    setGrid(next);

    // Check error
    const newErr = { ...errorCells };
    if (n !== 0 && n !== correct) {
      newErr[`${r}-${c}`] = true;
      if (soundOn) speakText("Wrong, try again");
    } else {
      delete newErr[`${r}-${c}`];
      if (n !== 0 && soundOn) speakText(`${n}`);
    }
    setErrorCells(newErr);

    // Check win
    let complete = true;
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) {
      if (next[i][j] !== puzzle.sol[i][j]) complete = false;
    }
    if (complete) {
      setWon(true);
      recordGameWon("sudoku");
      if (soundOn) speakText("Great! Sudoku complete!");
    }
  };

  const handleCellClick = (r, c) => {
    if (isHint(r, c) || won) return;
    setSelected({ r, c });
  };

  const erase = () => tryPlace(0);
  const hint = useCallback(() => {
    // Find first empty correct cell
    for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) {
      if (!isHint(r, c) && grid[r][c] !== puzzle.sol[r][c]) {
        const next = grid.map(row => [...row]);
        next[r][c] = puzzle.sol[r][c];
        setGrid(next);
        if (soundOn) speakText(`Hint! ${puzzle.sol[r][c]}`);
        return;
      }
    }
  }, [grid, puzzle, soundOn]);

  const reset = () => {
    setGrid(PUZZLES[puzzleIdx].hints.map(r => [...r]));
    setSelected(null); setErrorCells({}); setWon(false);
  };

  const next = () => {
    const ni = (puzzleIdx + 1) % PUZZLES.length;
    setPuzzleIdx(ni);
    setGrid(PUZZLES[ni].hints.map(r => [...r]));
    setSelected(null); setErrorCells({}); setWon(false);
  };

  const cellSize = Math.min((winW - 80) / 4, 110);

  return (
    <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-3 pt-3" data-testid="sudoku-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🧩 Puzzle {puzzleIdx + 1}/{PUZZLES.length}</span>
        <div className="flex gap-1.5">
          <Button data-testid="sudoku-hint" onClick={hint} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Lightbulb className="w-3 h-3" /></Button>
          <Button data-testid="sudoku-reset" onClick={reset} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><RotateCcw className="w-3 h-3" /></Button>
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <p className="font-fredoka text-gold-light/70 text-xs mb-3 text-center">Isi setiap baris, kolom & kotak 2×2 dengan angka 1-4</p>

      {/* Grid */}
      <div className="bg-amber-900/30 p-2 rounded-2xl border-4 border-gold/40 mb-3" data-testid="sudoku-grid">
        {grid.map((row, r) => (
          <div key={`row-${r}`} className="flex">
            {row.map((val, c) => {
              const hint = isHint(r, c);
              const sel = selected?.r === r && selected?.c === c;
              const err = errorCells[`${r}-${c}`];
              const borderR = c === 1 ? "border-r-4" : "border-r";
              const borderB = r === 1 ? "border-b-4" : "border-b";
              return (
                <button key={`cell-${r}-${c}`} data-testid={`sudoku-cell-${r}-${c}`}
                  onClick={() => handleCellClick(r, c)}
                  className={`${borderR} ${borderB} border-amber-900/50 flex items-center justify-center font-fredoka font-bold text-5xl transition-all
                    ${hint ? "bg-amber-100 text-slate-900" : "bg-white"}
                    ${sel ? "bg-yellow-300 ring-4 ring-gold scale-105" : ""}
                    ${err ? "bg-red-200 text-red-700" : ""}
                    ${!hint && !err ? "text-blue-700" : ""}
                  `}
                  style={{ width: cellSize, height: cellSize }}>
                  {val !== 0 ? val : ""}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Number pad */}
      <div className="grid grid-cols-4 gap-3 w-full mb-3">
        {[1, 2, 3, 4].map(n => (
          <motion.button key={n} data-testid={`sudoku-num-${n}`}
            onClick={() => tryPlace(n)}
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.9 }}
            className="ingredient-3d rounded-2xl py-5 font-fredoka font-bold text-5xl text-slate-800">
            {n}
          </motion.button>
        ))}
      </div>
      <Button data-testid="sudoku-erase" onClick={erase} className="w-full glass-card border-gold/20 text-gold-light font-fredoka rounded-xl h-11 text-base">🧹 Hapus</Button>

      <AnimatePresence>
        {won && (
          <>
            <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" data-testid="sudoku-win">
              <div className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
                <span className="text-6xl block mb-3">🏆</span>
                <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Hebat!</h2>
                <p className="font-fredoka text-gold-light/80 mb-4">Sudoku selesai!</p>
                <div className="flex flex-col gap-2">
                  <Button onClick={next} className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500">Puzzle Baru</Button>
                  <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SudokuAnak;
